import { MapPin, ArrowUpRight, Wind, Home as HomeIcon, SunMedium, Check } from "lucide-react";
import { PROJECTS, BUSINESS } from "@/lib/siteData";

const ADDITIONAL_SERVICES = [
  {
    id: "gutter-overview",
    icon: Wind,
    title: "Gutter Cleaning & Clearing",
    body:
      "Another recent project involved gutter cleaning and clearing, where we removed compacted moss, leaves and debris that had built up over the years. Keeping gutters clear allows rainwater to flow properly and helps prevent costly water damage — damp coming through the walls, fascias and soffits, all caused by overflowing gutters.",
  },
  {
    id: "roof-overview",
    icon: HomeIcon,
    title: "Roof Cleaning Across Essex",
    body:
      "Alongside gutter maintenance, we also provide professional roof cleaning throughout Essex. Moss, algae and lichen can hold moisture against roof tiles, reducing their lifespan and causing unnecessary wear. Our process safely removes all the dirt and organic growth before applying a biocide treatment to inhibit regrowth — keeping roofs cleaner for longer and lifting kerb appeal.",
  },
  {
    id: "solar-overview",
    icon: SunMedium,
    title: "Solar Panel Cleaning",
    body:
      "As part of our exterior cleaning services, we also offer solar panel cleaning. Dirt, lichen, bird droppings and airborne pollution can reduce the efficiency of your panels over time. Professional cleaning helps maximise the amount of sunlight reaching the panels — restoring performance while respecting the manufacturer's recommendations.",
  },
];

const FULL_SERVICE_LIST = [
  "Roof Cleaning",
  "Solar Panel Cleaning",
  "Gutter Cleaning & Clearances",
  "Driveway & Patio Cleaning",
  "Window Cleaning",
  "Tarmac Restoration",
  "Biocide Treatments",
  "Wheelie Bin Cleaning",
  "Fascia & Soffit Cleaning",
  "Soft Washing",
  "Oil & Rust Removal",
];

const ProjectRow = ({ project, index }) => {
  const reversed = index % 2 === 1;
  return (
    <article
      data-testid={`project-row-${project.id}`}
      className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center"
    >
      <div
        className={`lg:col-span-6 ${reversed ? "lg:order-2" : ""}`}
      >
        <div className="relative rounded-2xl overflow-hidden shadow-xl shadow-blue-900/10 border border-slate-100">
          <img
            src={project.image}
            alt={`${project.title} — before and after`}
            loading="lazy"
            className="w-full h-auto object-cover"
          />
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            {project.tags.map((t) => (
              <span
                key={t}
                className="px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] uppercase tracking-widest font-bold text-blue-700 shadow-sm"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className={`lg:col-span-6 ${reversed ? "lg:order-1" : ""}`}>
        <div className="text-xs text-slate-400 font-mono mb-2">
          PROJECT {String(index + 1).padStart(2, "0")}
        </div>
        <h3
          className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {project.title}
        </h3>
        <div className="mt-2 flex items-center gap-1 text-sm text-slate-500">
          <MapPin className="w-4 h-4" /> {project.location}
        </div>
        <p className="mt-5 text-base lg:text-lg text-slate-600 leading-relaxed">
          {project.description}
        </p>
      </div>
    </article>
  );
};

export const RecentProjects = () => {
  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="py-20 sm:py-28 lg:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="inline-block text-green-700 font-semibold text-sm uppercase tracking-widest">
            Recent projects
          </span>
          <h2
            data-testid="projects-headline"
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Our recent exterior cleaning projects across Essex.
          </h2>
          <p className="mt-5 text-base lg:text-lg text-slate-600 leading-relaxed">
            At Alfa Exterior Cleaning, we&apos;ve recently completed a variety of
            jobs across north and south Essex — helping homeowners restore their
            property&apos;s appearance and protect it from long-term damage.
          </p>
        </div>

        {/* Photo-backed project write-ups */}
        <div className="mt-16 space-y-20">
          {PROJECTS.map((p, i) => (
            <ProjectRow key={p.id} project={p} index={i} />
          ))}
        </div>

        {/* Additional service overviews (text-only) */}
        <div className="mt-24">
          <h3
            className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight max-w-3xl"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            More of what we&apos;ve been up to.
          </h3>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {ADDITIONAL_SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.id}
                  data-testid={`project-overview-${s.id}`}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 lg:p-7"
                >
                  <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h4
                    className="mt-4 text-lg font-bold text-slate-900"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {s.title}
                  </h4>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    {s.body}
                  </p>
                </article>
              );
            })}
          </div>
        </div>

        {/* Closing summary + full service list */}
        <div className="mt-20 bg-white rounded-3xl border border-slate-100 shadow-sm p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3">
              <h3
                className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Whatever the job, we deliver high-quality results.
              </h3>
              <p className="mt-5 text-base lg:text-lg text-slate-600 leading-relaxed">
                Roof cleaning, solar panels, gutters, driveways and patios — or
                any of our other exterior cleaning services — we work with
                professional equipment, specialist chemicals and proper attention
                to detail.
              </p>
              <p className="mt-4 text-base lg:text-lg text-slate-600 leading-relaxed">
                If you&apos;re based in Colchester, Essex or the surrounding
                areas, we&apos;d be happy to provide you with a free and
                no-obligation quote.
              </p>
              <p
                className="mt-6 text-lg font-bold text-green-700"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                As we like to say — Goodbye grime, hello shine.
              </p>

              <a
                href="#contact"
                data-testid="projects-cta"
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-blue-700 hover:bg-blue-800 text-white font-semibold transition-colors"
              >
                Request your free quote
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>

            <div className="lg:col-span-2">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-4">
                Our exterior cleaning services
              </div>
              <ul className="space-y-2.5">
                {FULL_SERVICE_LIST.map((s) => (
                  <li
                    key={s}
                    className="flex items-start gap-2.5 text-slate-700"
                  >
                    <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-green-100 text-green-700 flex items-center justify-center">
                      <Check className="w-3 h-3" />
                    </span>
                    <span className="text-sm sm:text-base">{s}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-slate-500">
                Contact us today for your free quote — let us help restore your
                property&apos;s exterior.
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Call{" "}
                <a
                  href={`tel:${BUSINESS.phoneTel}`}
                  className="text-blue-700 font-semibold"
                >
                  {BUSINESS.phone}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecentProjects;
